//@ts-nocheck
import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { useCheckForOwnership, userTypes } from '../../auth/userPermissions';
import { featureSelector } from '../../../new-store/features/selectors';

interface IProps {
    code: string
    checkOwnership?: boolean
    bypassAccess?: boolean
    children: ReactElement
}

export enum AccessType {
    NoAccess = 'NA',
    ReadOnly = 'RO',
    ReadWrite = 'RW'
}

const MEDICAL_PROFILES = ['MSL', 'Medical Manager'];

export const checkIfUserHasMedicalProfile = (user) => {
    if (!user || !user.profile) return false;

    return MEDICAL_PROFILES.includes(user.profile.name);
}

const voteOnFeature = (feature, rwCallback, roCallback, naCallback, defaultCallback) => {
    switch (feature?.accessType) {
        case AccessType.ReadWrite:
            return rwCallback();
        case AccessType.ReadOnly:
            return roCallback();
        case AccessType.NoAccess:
            return naCallback();
        default:
            return defaultCallback();
    }
}

const ACCESS_TYPE_PRIORITY = {
    [AccessType.ReadWrite]: 3,
    [AccessType.ReadOnly]: 2,
    [AccessType.NoAccess]: 1,
};

/**
 * The same feature code can appear multiple times in a list (e.g. granted through
 * several countries). Rather than taking the first match — which could be an
 * NA entry while an RW entry for the same code exists further in the list — pick
 * the entry with the highest access level: RW > RO > NA > (unrecognized).
 */
const pickBestFeatureMatch = (list, matches) => {
    return list?.reduce((best, item) => {
        if (!matches(item)) return best;
        if (!best) return item;

        const bestScore = ACCESS_TYPE_PRIORITY[best.accessType] ?? 0;
        const itemScore = ACCESS_TYPE_PRIORITY[item.accessType] ?? 0;
        return itemScore > bestScore ? item : best;
    }, undefined);
};

export const checkReadOnly = (featureState, code) => {
    const profileFeatures = featureState.profile.data;
    const countryFeatures = featureState.country.data;

    const profileFeature = pickBestFeatureMatch(profileFeatures, pf => pf.feature.code === code);
    const countryFeature = pickBestFeatureMatch(countryFeatures, cf => cf.feature.code === code);

    return countryFeature?.accessType === AccessType.ReadOnly
        ? true
        : profileFeature?.accessType === AccessType.ReadOnly;
}

export const checkAccessControl = (featureState, code) => {
    const profileFeatures = featureState.profile.data;
    const countryFeatures = featureState.country.data;

    const profileFeature = pickBestFeatureMatch(profileFeatures, pf => pf.feature.code === code);
    const countryFeature = pickBestFeatureMatch(countryFeatures, cf => cf.feature.code === code);

    return voteOnFeature(
        countryFeature,
        () => voteOnFeature(profileFeature, () => true, () => true, () => false, () => true),
        () => voteOnFeature(profileFeature, () => true, () => true, () => false, () => true),
        () => false,
        () => voteOnFeature(profileFeature, () => true, () => true, () => false, () => false)
    );
}

export const getAccessTypeForProfileFeature = (featureState, code) => {
    const profileFeatures = featureState.profile.data;
    const profileFeature = pickBestFeatureMatch(profileFeatures, pf => pf.feature.code === code);
    return profileFeature?.accessType;
}

export const checkAccessForCountry = (featureState, country, code) => {
    const countryFeatures = featureState.country.data;
    const countryFeature = pickBestFeatureMatch(countryFeatures, cf => cf.feature.code === code && cf.country.id === country.id);

    return voteOnFeature(countryFeature, () => true, () => true, () => false, () => false);
}

export const isEmeaProfile = (user) => {
    return !!user.isEmea;
}

const AccessControl = ({ code, checkOwnership = false, bypassAccess = false, children }: IProps) => {
    const ownership = useCheckForOwnership();
    const featureState = useSelector(featureSelector);
    const profileFeatures = featureState.profile.data;
    const countryFeatures = featureState.country.data;

    if (checkOwnership && ownership.user === userTypes.notOwner) {
        return null;
    }
    if (!!bypassAccess) {
        return children;
    }

    const profileFeature = pickBestFeatureMatch(profileFeatures, pf => pf.feature.code === code);
    const countryFeature = pickBestFeatureMatch(countryFeatures, cf => cf.feature.code === code);

    return voteOnFeature(
        countryFeature,
        () => voteOnFeature(profileFeature, () => children, () => React.cloneElement(children, { readOnly: true }), () => null, () => null),
        () => voteOnFeature(profileFeature, () => children, () => React.cloneElement(children, { readOnly: true }), () => null, () => null),
        () => false,
        () => voteOnFeature(profileFeature, () => children, () => React.cloneElement(children, { readOnly: true }), () => null, () => null),
    );
}

export default AccessControl;
